import { Injectable, inject } from '@angular/core';
import { singleConnect } from '@core/utils';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import {
	Action,
	State,
	StateToken,
	createPropertySelectors,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { of, retry, timer } from 'rxjs';

import { ClientIntakeFormItemsSubCollectionRepo } from '../clientIntakeFormItems.firebase.repo';

import type {
	ClientIntakeFormItemsStateModel,
	EngineFormItem,
} from './clientIntakeFormItems.firebase.models';
import {
	ClearClientIntakeFormItemsConnectionAndState,
	SetClientIntakeFormItems,
	SetClientIntakeFormItemsConnection,
	UpsertClientIntakeFormItems,
	UpdateClientIntakeFormItem,
	DeleteClientIntakeFormItem,
} from './clientIntakeFormItems.firebase.actions';

export const CLIENT_INTAKE_FORM_ITEMS_STATE_TOKEN =
	new StateToken<ClientIntakeFormItemsStateModel>('clientIntakeFormItems');

export const $PROPS = createPropertySelectors(
	CLIENT_INTAKE_FORM_ITEMS_STATE_TOKEN,
);

export const CLIENT_INTAKE_FORM_ITEMS_STATE_DEFAULTS: ClientIntakeFormItemsStateModel =
	{
		query: {
			connected: false,
			pathParams: null,
		},
		items: [],
	};

@State<ClientIntakeFormItemsStateModel>({
	name: CLIENT_INTAKE_FORM_ITEMS_STATE_TOKEN,
	defaults: CLIENT_INTAKE_FORM_ITEMS_STATE_DEFAULTS,
})
@Injectable()
export class ClientIntakeFormItemsState implements NgxsOnInit {
	private subCollectionRepo = inject(ClientIntakeFormItemsSubCollectionRepo);
	static _props = $PROPS;

	ngxsOnInit(): void {
		singleConnect(
			ClientIntakeFormItemsState._props.query,
			(query) => {
				if (
					!query.connected ||
					!query.pathParams?.businessId ||
					!query.pathParams?.matterId ||
					!query.pathParams?.clientIntakeFormId
				) {
					return of([] as EngineFormItem[]);
				}

				return this.subCollectionRepo
					.getCollection(query.pathParams)
					.getAll()
					.pipe(
						retry({
							count: 10,
							delay: (error, retryCount) => {
								const backoffTime = Math.min(
									500 * Math.pow(2, retryCount - 1),
									30000,
								);
								return timer(backoffTime);
							},
						}),
					);
			},
			(data) => {
				return new SetClientIntakeFormItems(data);
			},
		);
	}

	@Action(SetClientIntakeFormItemsConnection)
	setClientIntakeFormItemsConnection(
		ctx: StateContext<ClientIntakeFormItemsStateModel>,
		action: SetClientIntakeFormItemsConnection,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: action.connect,
					pathParams: action.pathParams,
				}),
			}),
		);
	}

	@Action(ClearClientIntakeFormItemsConnectionAndState)
	clearClientIntakeFormItemsConnection(
		ctx: StateContext<ClientIntakeFormItemsStateModel>,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: false,
					pathParams: null,
				}),
				items: [],
			}),
		);
	}

	@Action(SetClientIntakeFormItems)
	setClientIntakeFormItems(
		ctx: StateContext<ClientIntakeFormItemsStateModel>,
		{ items }: SetClientIntakeFormItems,
	): void {
		ctx.patchState({
			items,
		});
	}

	@Action(UpsertClientIntakeFormItems)
	async upsertClientIntakeFormItems(
		ctx: StateContext<ClientIntakeFormItemsStateModel>,
		{ items }: UpsertClientIntakeFormItems,
	): Promise<void> {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams) return;

			const batchItems = items.map((item) => ({
				id: item.item.id as string,
				data: item,
			}));

			await this.subCollectionRepo
				.getCollection(pathParams)
				?.setBatch(batchItems);
		} catch (err) {
			const itemIds = items?.map((item) => item.item.id) ?? [];
			console.error(
				`Error upserting client intake form items (IDs: ${JSON.stringify(itemIds)}):`,
				err,
			);
		}
	}

	@Action(UpdateClientIntakeFormItem)
	updateClientIntakeFormItem(
		ctx: StateContext<ClientIntakeFormItemsStateModel>,
		{ itemId, updates }: UpdateClientIntakeFormItem,
	): void {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams) return;

			void this.subCollectionRepo
				.getCollection(pathParams)
				?.update(itemId, updates);
		} catch (err) {
			console.error(
				`Error updating client intake form item (ID: ${itemId}):`,
				err,
			);
		}
	}

	@Action(DeleteClientIntakeFormItem)
	deleteClientIntakeFormItem(
		ctx: StateContext<ClientIntakeFormItemsStateModel>,
		{ itemId }: DeleteClientIntakeFormItem,
	): void {
		try {
			const pathParams = ctx.getState().query.pathParams;

			if (!pathParams) return;

			void this.subCollectionRepo.getCollection(pathParams)?.delete(itemId);
		} catch (err) {
			console.error(
				`Error deleting client intake form item (ID: ${itemId}):`,
				err,
			);
		}
	}
}
